import {
  Edit,
  Clock,
  Eye,
  History,
  MessageSquare,
  MoreHorizontal,
  Share2,
  Star,
  Trash2,
  User,
  Download,
} from 'lucide-react'
import { useState, useEffect } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useDocument,
  useWatchDocument,
  useUnwatchDocument,
  useExportDocumentMarkdown,
  useExportDocumentPdf,
} from '@/hooks'
import { cn } from '@/lib/utils'

import { DocumentBreadcrumb } from './breadcrumb'
import { CommentSection } from './comment-section'
import { VersionHistory } from './version-history'

interface DocumentViewerProps {
  documentId: string
  spaceId?: string
  onEdit?: () => void
  onDelete?: () => void
  className?: string
  showVersionHistory?: boolean
  onVersionHistoryChange?: (show: boolean) => void
}

/**
 * 文档查看器组件
 * 显示文档内容、元信息、版本历史和评论
 */
export function DocumentViewer({
  documentId,
  onEdit,
  onDelete,
  className,
  showVersionHistory,
  onVersionHistoryChange,
}: DocumentViewerProps) {
  const { data: document, isLoading } = useDocument(documentId)
  const [activeTab, setActiveTab] = useState<'content' | 'history' | 'comments'>('content')
  const watchDocument = useWatchDocument()
  const unwatchDocument = useUnwatchDocument()
  const exportMarkdown = useExportDocumentMarkdown()
  const exportPdf = useExportDocumentPdf()
  const [isWatching, setIsWatching] = useState(false)

  // 外部控制版本历史显示
  useEffect(() => {
    if (showVersionHistory) {
      setActiveTab('history')
      onVersionHistoryChange?.(false)
    }
  }, [showVersionHistory, onVersionHistoryChange])

  const handleToggleWatch = () => {
    if (isWatching) {
      unwatchDocument.mutate(documentId, {
        onSuccess: () => setIsWatching(false),
      })
    } else {
      watchDocument.mutate(documentId, {
        onSuccess: () => setIsWatching(true),
      })
    }
  }

  const handleExportMarkdown = () => {
    exportMarkdown.mutate(documentId)
  }

  const handleExportPdf = () => {
    exportPdf.mutate()
  }

  if (isLoading) {
    return <DocumentViewerSkeleton />
  }

  if (!document) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center">
        文档不存在或已被删除
      </div>
    )
  }

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {/* Header */}
      <div className="border-b px-4 py-3">
        <DocumentBreadcrumb documentId={documentId} />

        <div className="mt-3 flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-bold">{document.title}</h1>
            <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1">
                <User className="h-4 w-4" />
                {document.author?.name ?? '未知作者'}
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {document.updatedAt
                  ? `更新于 ${formatDate(document.updatedAt)}`
                  : `创建于 ${formatDate(document.createdAt)}`}
              </span>
              {document.tags && document.tags.length > 0 && (
                <div className="flex items-center gap-1">
                  {document.tags.map((tag) => (
                    <span key={tag} className="bg-muted rounded px-1.5 py-0.5 text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleToggleWatch}>
              <Star
                className={cn('mr-2 h-4 w-4', isWatching && 'fill-yellow-400 text-yellow-400')}
              />
              {isWatching ? '取消关注' : '关注'}
            </Button>
            <Button variant="default" size="sm" onClick={onEdit}>
              <Edit className="mr-2 h-4 w-4" />
              编辑
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>导出</DropdownMenuLabel>
                <DropdownMenuItem onClick={handleExportMarkdown}>
                  <Download className="mr-2 h-4 w-4" />
                  Markdown (.md)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPdf}>
                  <Download className="mr-2 h-4 w-4" />
                  PDF (打印)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Share2 className="mr-2 h-4 w-4" />
                  分享
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDelete} className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  删除
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Tabs */}
        <div className="-mb-px mt-4 flex gap-4 border-b">
          <TabButton
            active={activeTab === 'content'}
            onClick={() => setActiveTab('content')}
            icon={Eye}
            label="内容"
          />
          <TabButton
            active={activeTab === 'history'}
            onClick={() => setActiveTab('history')}
            icon={History}
            label="版本历史"
          />
          <TabButton
            active={activeTab === 'comments'}
            onClick={() => setActiveTab('comments')}
            icon={MessageSquare}
            label="评论"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'content' && <DocumentContent content={document.content} />}
        {activeTab === 'history' && <VersionHistory documentId={documentId} />}
        {activeTab === 'comments' && <CommentSection documentId={documentId} />}
      </div>
    </div>
  )
}

// ==================== Sub Components ====================

interface TabButtonProps {
  active: boolean
  onClick: () => void
  icon: typeof Eye
  label: string
}

function TabButton({ active, onClick, icon: Icon, label }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 border-b-2 px-1 pb-2 text-sm font-medium transition-colors',
        active
          ? 'border-primary text-primary'
          : 'text-muted-foreground hover:text-foreground border-transparent'
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  )
}

interface DocumentContentProps {
  content: string
}

function DocumentContent({ content }: DocumentContentProps) {
  // 简单的 Markdown 渲染（实际项目中应使用 react-markdown 或类似库）
  const renderContent = () => {
    // 基础 Markdown 转换
    const html = content
      // 标题
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-6 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-8 mb-4">$1</h1>')
      // 粗体和斜体
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // 代码
      .replace(/`(.+?)`/g, '<code class="rounded bg-muted px-1 py-0.5 text-sm">$1</code>')
      // 链接
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary underline">$1</a>')
      // 列表
      .replace(/^- (.+$)/gim, '<li class="ml-4">$1</li>')
      .replace(/^\d+\. (.+$)/gim, '<li class="ml-4 list-decimal">$1</li>')
      // 段落
      .replace(/\n\n/g, '</p><p class="my-3">')

    return `<p class="my-3">${html}</p>`
  }

  return (
    <div
      className="prose prose-sm dark:prose-invert max-w-none p-6"
      dangerouslySetInnerHTML={{ __html: renderContent() }}
    />
  )
}

function DocumentViewerSkeleton() {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-4 py-3">
        <Skeleton className="h-4 w-48" />
        <div className="mt-3">
          <Skeleton className="h-8 w-64" />
          <div className="mt-2 flex gap-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="mt-4 flex gap-4">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-16" />
        </div>
      </div>
      <div className="flex-1 p-6">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="mt-2 h-4 w-3/4" />
        <Skeleton className="mt-2 h-4 w-5/6" />
        <Skeleton className="mt-4 h-4 w-full" />
        <Skeleton className="mt-2 h-4 w-2/3" />
      </div>
    </div>
  )
}

// ==================== Helpers ====================

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
